import { css, cx } from "@emotion/css";
import { endOfDay, format, startOfDay, subDays } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from "src/components/Default/Calendar/DateRangePicker";
import { GreyButton } from "src/components/Default/LekUI/Button";
import Box from "src/components/Default/Main/Box";
import { Header } from "src/components/Main/ExamsBox/ExamsBox";
import { ExamCategoriesStats } from "src/components/Modals/StatisticsModals/ExamCategoriesStats";
import { ExamInfoStats } from "src/components/Modals/StatisticsModals/ExamInfoStats";
import type { ExamStats } from "src/components/Modals/StatisticsModals/examStatsTypes";
import { Loading } from "src/components/TextEditor";
import isMobile from "src/helpers/isMobile";
import { getAppConfig } from "src/hooks/useAppConfig";
import { useGetUserStats } from "src/hooks/useChartsData";
import CalendarIcon from "src/images/Icons/newIcons/calendar.svg";
import ChartIcon from "src/images/Icons/newIcons/chart.svg";

const getInitialValues = (): [Date, Date] => {
	const today = new Date();
	const startDate = subDays(today, 13);

	return [startOfDay(startDate), endOfDay(today)];
};

export const UserStatisticsModal = () => {
	const { statisticsDateRangeInDays } = getAppConfig();
	const [dates, setDates] = useState<[Date, Date]>(getInitialValues());

	const {
		data: stats,
		isLoading,
		isFetching,
	} = useGetUserStats({
		startDate: dates[0],
		endDate: dates[1],
	}) as { data?: ExamStats | null; isLoading: boolean; isFetching: boolean };

	const { t } = useTranslation();
	const [selectedTab, setSelectedTab] = useState(0);

	const renderContent = () => {
		if (!stats) {
			return null;
		}

		return selectedTab === 0 ? (
			<ExamInfoStats stats={stats} showScore={false} />
		) : (
			<ExamCategoriesStats departments={stats.departmentsStats ?? []} />
		);
	};

	if (isLoading || stats === null) {
		return null;
	}

	return (
		<Box
			tabs={[
				t("modals.statistics.tabs.sectionInfo"),
				t("modals.statistics.tabs.sectionStats"),
			]}
			onTabsChange={setSelectedTab}
			selectedTab={selectedTab}
			Header={
				<Header
					title={t("modals.statistics.title")}
					icon={<ChartIcon className={cx("icon-big")} />}
					Search={
						<div>
							{/* @ts-expect-error Loading is untyped */}
							<Loading isLoading={isFetching} size={30}>
								<DateRangePicker
									dates={dates}
									setDates={setDates}
									MAX_DAYS={statisticsDateRangeInDays}
									renderTrigger={({
										setAnchor,
										anchor,
										isDateSelected,
										selectedRange,
									}: {
										setAnchor: (e: React.MouseEvent<HTMLElement>) => void;
										anchor: HTMLElement | null;
										isDateSelected: boolean;
										selectedRange: { from: Date; to: Date };
									}) => {
										return (
											<GreyButton
												onClick={setAnchor}
												buttonRef={anchor as unknown as React.Ref<HTMLButtonElement>}
												endIcon={<CalendarIcon className="icon-normal" />}
												className={cx(
													"flex justify-around items-center",
													css`
                            min-width: 150px;
                          `,
												)}
											>
												<span
													className={cx("size-14", {
														"mt-0 font-bold": isDateSelected,
													})}
												>
													{isDateSelected
														? `${format(selectedRange.from, "dd.MM")} - ${format(selectedRange.to, "dd.MM")}`
														: t("datePicker.selectDate")}
												</span>
											</GreyButton>
										);
									}}
								/>
							</Loading>
						</div>
					}
				/>
			}
		>
			<div>
				<div className="user-statistics-modal--divider"></div>
			</div>
			<div
				className={cx({
					"content-box": isMobile(),
				})}
			>
				{renderContent()}
			</div>
		</Box>
	);
};
